import type { Dispatch } from 'redux'
import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { timeout, delay } from '@ynap/promise-utils'
import { FETCH_PROMOTIONS_SUCCESS, FETCH_PROMOTIONS_REQUEST, FETCH_PROMOTIONS_ERROR } from '../constants/ActionTypes'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getLocale } from '@ynap/cart-state'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { Promotion } from 'app/types/common-types'
import { StoreState } from 'app/state/types'

type FetchPromotionResponseData = {
    Promotion: Promotion[]
}

function fetchPromotionsRequest() {
    return {
        type: FETCH_PROMOTIONS_REQUEST,
    }
}

function fetchPromotionsSuccess(payload: { id: Promotion['promotionId']; name: Promotion['administrativeName']; description: string }[]) {
    return {
        type: FETCH_PROMOTIONS_SUCCESS,
        payload,
    }
}

function fetchPromotionsError(payload: unknown) {
    return {
        type: FETCH_PROMOTIONS_ERROR,
        payload,
    }
}

export default function fetchPromotions() {
    return (dispatch: Dispatch, getState: () => StoreState, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        dispatch(fetchPromotionsRequest())

        const request = Cart.fetchPromotions({ ...watsonConfig, langId, additionalHeaders, active: true })

        return delay(timeout(fetch(request), getTimeout(state)), getDelay(state))
            .then((response: Response) => {
                if (!response.ok) {
                    throw new ResponseError(response)
                }
                return response.json()
            })
            .then((data: FetchPromotionResponseData) => {
                const { Promotion = [] } = data

                const payload = Promotion.map((promotion) => {
                    const { Description, promotionId, administrativeName } = promotion

                    const description = Description?.map((description) => description.longDescription || description.shortDescription).join(' ') ?? ''

                    return {
                        id: promotionId,
                        name: administrativeName,
                        description,
                    }
                })
                dispatch(fetchPromotionsSuccess(payload))
            })
            .catch((error: unknown) => {
                dispatch(fetchPromotionsError(error))
                throw error
            })
    }
}
